import React from 'react'
import {
  GroupedResults,
  SearchHit,
  SearchScope,
  flattenSearchResults,
} from '@nyos/shared'

/**
 * SearchResultsPanel — anchored dropdown under the GlobalHeader input.
 * Renders the ARIA listbox half of the combobox: scope chips, grouped
 * hits (cars → tracks → switch lists), recents when the box is empty,
 * and per-scope empty states. Selection is delegated via onSelect;
 * keyboard state (active index) stays in the parent so ↑↓/Enter work
 * while DOM focus remains on the input.
 */

interface SearchResultsPanelProps {
  query: string
  results: GroupedResults
  recents: string[]
  scope: SearchScope
  onScopeChange: (scope: SearchScope) => void
  activeIndex: number
  onHoverIndex: (index: number) => void
  onSelect: (hit: SearchHit) => void
  onRecentClick: (term: string) => void
  isLoading: boolean
  listboxId: string
}

const SCOPES: { id: SearchScope; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'cars', label: 'Cars' },
  { id: 'tracks', label: 'Tracks' },
  { id: 'jobs', label: 'Switch Lists' },
]

const optionId = (i: number) => `yard-search-opt-${i}`

export { optionId as searchOptionId }

const SearchResultsPanel: React.FC<SearchResultsPanelProps> = ({
  query,
  results,
  recents,
  scope,
  onScopeChange,
  activeIndex,
  onHoverIndex,
  onSelect,
  onRecentClick,
  isLoading,
  listboxId,
}) => {
  const flat = flattenSearchResults(results)
  const hasQuery = query.trim().length > 0

  const row = (hit: SearchHit, index: number) => {
    const title =
      hit.kind === 'car' ? hit.reportingMark : hit.kind === 'track' ? hit.name : hit.woNumber ?? hit.id.slice(0, 8)
    const detail =
      hit.kind === 'car'
        ? hit.trackId
          ? `${hit.trackName ?? 'On track'}${hit.linearOffset != null ? ` · ${Math.round(hit.linearOffset)} ft` : ''}`
          : 'Not on a track'
        : hit.kind === 'track'
          ? (hit.trackType ?? 'Track')
          : hit.swlNumber
            ? `SWL ${hit.swlNumber}`
            : (hit.status ?? '')
    return (
      <div
        key={`${hit.kind}-${hit.id}`}
        id={optionId(index)}
        role="option"
        aria-selected={index === activeIndex}
        onMouseEnter={() => onHoverIndex(index)}
        onMouseDown={(e) => {
          // mousedown (not click): fires before input blur closes the panel.
          e.preventDefault()
          onSelect(hit)
        }}
        className={`px-3 py-2 cursor-pointer flex items-center justify-between gap-3 text-sm ${
          index === activeIndex ? 'bg-hover text-text-primary' : 'text-text-primary'
        }`}
      >
        <span className="font-medium truncate">{title}</span>
        <span className="text-xs text-text-muted truncate">{detail}</span>
      </div>
    )
  }

  const group = (label: string, hits: SearchHit[], startIndex: number) =>
    hits.length > 0 && (
      <div key={label}>
        <div className="px-3 pt-2 pb-1 text-xs font-semibold uppercase tracking-wider text-text-muted">
          {label}
        </div>
        {hits.map((h, i) => row(h, startIndex + i))}
      </div>
    )

  return (
    <div className="absolute top-full left-0 right-0 mt-1 z-50 rounded-md border border-border bg-card shadow-xl overflow-hidden">
      <div className="flex gap-1 px-2 py-1.5 border-b border-border">
        {SCOPES.map((s) => (
          <button
            key={s.id}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onScopeChange(s.id)}
            className={`px-2 py-0.5 rounded text-xs font-medium transition-colors ${
              scope === s.id
                ? 'bg-accent-blue text-white'
                : 'text-text-muted hover:text-text-primary hover:bg-hover'
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>
      <div role="listbox" id={listboxId} className="max-h-[320px] overflow-y-auto">
        {isLoading && hasQuery && (
          <div className="px-3 py-3 text-xs text-text-muted">Searching…</div>
        )}
        {!hasQuery && recents.length > 0 && (
          <div>
            <div className="px-3 pt-2 pb-1 text-xs font-semibold uppercase tracking-wider text-text-muted">
              Recent
            </div>
            {recents.map((r) => (
              <div
                key={r}
                onMouseDown={(e) => {
                  e.preventDefault()
                  onRecentClick(r)
                }}
                className="px-3 py-1.5 cursor-pointer text-sm text-text-primary hover:bg-hover"
              >
                {r}
              </div>
            ))}
          </div>
        )}
        {hasQuery && (
          <>
            {group('Cars', results.cars, 0)}
            {group('Tracks', results.tracks, results.cars.length)}
            {group('Switch Lists', results.jobs, results.cars.length + results.tracks.length)}
            {!isLoading && flat.length === 0 && (
              <div className="px-3 py-4 text-center text-xs text-text-muted">
                No matches{scope !== 'all' ? ` in ${SCOPES.find((s) => s.id === scope)?.label}` : ''} for “{query.trim()}”
              </div>
            )}
          </>
        )}
      </div>
      <div className="px-3 py-1.5 border-t border-border text-xs text-text-muted">
        / focus · ↑↓ select · Enter jump · Esc close · car:/trk:/job: to scope
      </div>
    </div>
  )
}

export default React.memo(SearchResultsPanel)
